export default function AgentPagerSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            AgentPager — Support
          </h1>
          <p className="mt-1 text-xs text-[#8A93A2]">
            by <span className="font-semibold">Cosmovex</span> · formerly MDPilot
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            AgentPager keeps your AI coding agents one tap away: continue the
            Claude Code and Codex sessions you started on your computer, start
            new ones with Gemini CLI, OpenCode, Qwen Code or Goose, approve what
            they want to do, and get paged when they need you. Still stuck after
            reading this? Email{" "}
            <a
              className="text-[#0F6BBF] underline"
              href="mailto:trionapps@gmail.com"
            >
              trionapps@gmail.com
            </a>
            .
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Setting up</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-[#26313B]">
                How do I connect my computer?
              </h3>
              <p>
                On that computer, run <code>npx agentpager</code> (it needs
                Node 20 or newer; Mac, Linux and Windows all work). A QR code
                appears — scan it in the app, or tap &ldquo;Paste link
                instead&rdquo; and paste the link it printed. The code is
                single-use and expires after 10 minutes.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                I ran it and no QR code appeared.
              </h3>
              <p>
                That computer has paired with a phone before, so it goes
                straight to work instead of showing a code. Ask it for a fresh
                one with <code>npx agentpager pair</code>. Use the same command
                after reinstalling the app, clearing its data, or setting up a
                second phone.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                The app says my computer is offline.
              </h3>
              <p>
                AgentPager has to be running on it — that terminal window stays
                open, or run <code>agentpager service install</code> to start it
                automatically at login. Check with{" "}
                <code>agentpager devices</code>, and use{" "}
                <code>agentpager doctor</code> to see which agents that computer
                can actually drive.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                Which agents are supported?
              </h3>
              <p>
                Claude Code and Codex are driven directly. Gemini CLI, OpenCode
                (including DeepSeek), Qwen Code and Goose are driven through the
                Agent Client Protocol. The app shows only the ones that are
                actually installed and signed in on that computer.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                My project isn&apos;t in the list when I start a session.
              </h3>
              <p>
                Tap &ldquo;Browse folders on the computer&rdquo; and pick it.
                For safety, a computer only offers folders inside your home
                folder, the folder you started AgentPager in, or beside a
                project you already have a session in — and never hidden
                folders. If your code lives somewhere else entirely, start
                AgentPager from that folder once.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">Using it</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-[#26313B]">
                I&apos;m not getting notifications.
              </h3>
              <p>
                Open Settings in the app — the Notifications row shows whether
                they are on and can turn them back on if you declined. Android
                may also need AgentPager allowed under Settings → Apps →
                AgentPager → Notifications. Notifications only fire when you are
                not already looking at the app.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                Can a build or deploy page me too?
              </h3>
              <p>
                Yes — any script can. <code>agentpager notify &quot;AAB
                built&quot;</code> sends a notification, and{" "}
                <code>agentpager ask &quot;Deploy to production?&quot;</code>{" "}
                waits for your answer and exits 0 if you approve, 1 if you deny,
                or 2 if nobody could answer. Add <code>--timeout 5m</code> to
                change how long it waits (10 minutes by default); when the time
                runs out the request is taken off your phone so it cannot be
                approved late.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                Why did my approval not go through?
              </h3>
              <p>
                If your phone or your computer is offline, AgentPager tells you
                instead of pretending — the request stays on screen until it can
                actually be delivered. A request can also be withdrawn because
                you answered it at your desk or it ran out of time; the session
                says which.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                How does hands-free mode work?
              </h3>
              <p>
                Tap the microphone button, speak, and your words are read back
                before anything is sent. You can say &ldquo;approve&rdquo;,
                &ldquo;deny&rdquo;, &ldquo;status&rdquo; or &ldquo;switch
                to&rdquo; a project. Destructive actions are never approved by
                voice — those always need a press-and-hold. Voice commands are
                recognised in English; the rest of the app is in 15 languages.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                Where did my markdown editor go?
              </h3>
              <p>
                Nowhere — tap <strong>Toolkit</strong> at the top of the home
                screen. The editor with live preview, Mermaid diagrams, the
                CLAUDE.md / AGENTS.md builder, the prompt vault and the LAN
                Vault Server are all still there, and your documents are
                untouched.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">Pro</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-[#26313B]">What does Pro include?</h3>
              <p>
                Unlimited computers and sessions, hands-free voice mode,
                approval rules, live output all day, the LAN Vault Server, and
                no ads. The free version connects one computer, keeps one
                session live at a time, and streams a limited amount of live
                output each day — approvals and results still arrive after that.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                Is Pro a subscription or a one-time purchase?
              </h3>
              <p>
                Both are offered: a monthly subscription, which renews
                automatically until you cancel it in Google Play, or a one-time
                lifetime purchase. If you bought Pro when the app was called
                MDPilot, you keep everything — nothing was reset by the rename.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[#26313B]">
                I paid but the app still shows ads.
              </h3>
              <p>
                Open Settings → Restore purchases, using the same Google account
                that made the purchase. If it still doesn&apos;t appear, email
                us the order ID from your Google Play receipt.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">Privacy</h2>
          <p>
            Your code never leaves your computer. Your phone and your computer
            talk end-to-end encrypted, the relay in between cannot open those
            messages and deletes each one once it is delivered, and there is no
            account to create. The details are in the{" "}
            <a className="text-[#0F6BBF] underline" href="/apps/agentpager/privacy-policy">
              privacy policy
            </a>
            .
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Something is broken</h2>
          <p>
            Make sure the app and the computer side are both up to date
            (<code>npx agentpager@latest</code> always runs the newest bridge),
            restart the app, and check your connection. If it persists, email us
            your device model, Android version, and what you were doing — that
            is usually enough for us to reproduce it.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact</h2>
          <p>
            Email{" "}
            <a
              className="text-[#0F6BBF] underline"
              href="mailto:trionapps@gmail.com"
            >
              trionapps@gmail.com
            </a>{" "}
            — we usually reply within a couple of days.
          </p>
        </div>
      </section>
    </main>
  );
}
